<script setup>
// [TODO] Use Teleport to move the modal to the end of the body
import { ref, computed, defineEmits } from 'vue';
import { useEventListener } from '@vueuse/core';
import Button from 'dashboard/components-next/button/Button.vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  closeOnBackdropClick: { type: Boolean, default: true },
  showCloseButton: { type: Boolean, default: true },
  fullWidth: { type: Boolean, default: false },
  modalType: { type: String, default: 'centered' },
  size: { type: String, default: '' },
});

const emit = defineEmits(['close']);
const show = defineModel('show', { type: Boolean, default: false });

const { t } = useI18n();
const isDrawer = computed(() => props.modalType === 'right-aligned');
const widthClass = computed(() => {
  if (props.fullWidth) return 'h-full w-full rounded-none';
  if (isDrawer.value) return 'h-full w-full max-w-[30rem] rounded-none';
  const widths = {
    medium: 'w-[calc(100vw-1rem)] max-w-[960px]',
    compact: 'w-[calc(100vw-1rem)] max-w-md',
    'modal-big': 'w-[calc(100vw-1rem)] max-w-6xl',
  };
  return widths[props.size] || 'w-[calc(100vw-1rem)] max-w-lg';
});

// [TODO] Revisit this logic to use outside click directive
const mousedDownOnBackdrop = ref(false);

const handleMouseDown = () => {
  mousedDownOnBackdrop.value = true;
};

const close = () => {
  show.value = false;
  emit('close');
};

const onMouseUp = () => {
  if (mousedDownOnBackdrop.value) {
    mousedDownOnBackdrop.value = false;
    if (props.closeOnBackdropClick) {
      close();
    }
  }
};

const onKeydown = e => {
  if (show.value && e.code === 'Escape') {
    close();
    e.stopPropagation();
  }
};

useEventListener(document.body, 'mouseup', onMouseUp);
useEventListener(document, 'keydown', onKeydown);
</script>

<template>
  <Transition
    enter-active-class="transition-opacity duration-150 motion-reduce:transition-none"
    leave-active-class="transition-opacity duration-150 motion-reduce:transition-none"
    enter-from-class="opacity-0"
    leave-to-class="opacity-0"
  >
    <div
      v-if="show"
      class="modal-mask skip-context-menu fixed inset-0 z-[9990] flex items-center bg-black/40"
      :class="isDrawer ? 'right-aligned justify-end' : 'justify-center'"
      @mousedown="handleMouseDown"
    >
      <div
        role="dialog"
        aria-modal="true"
        class="modal-container skip-context-menu relative overflow-auto rounded border border-n-weak bg-n-surface-1 text-start shadow-2xl dark:border-[#363A42] dark:bg-[#222225]"
        :class="[
          widthClass,
          size,
          fullWidth || isDrawer ? 'max-h-full' : 'max-h-[calc(100dvh-1rem)]',
          fullWidth
            ? 'flex items-center justify-center'
            : '[&_.content]:p-4 [&_form]:px-4 [&_form]:py-4 [&_.modal-content]:px-4 [&_.modal-content]:py-4',
        ]"
        @mousedown.stop
      >
        <Button
          v-if="showCloseButton"
          type="button"
          size="sm"
          ghost
          slate
          icon="i-lucide-x"
          icon-size="lg"
          class="absolute end-3 top-2 z-10 !h-8 !w-8 !min-w-8 !p-1"
          :aria-label="t('GENERAL.CLOSE')"
          @click="close"
        />
        <slot />
      </div>
    </div>
  </Transition>
</template>
